import { apiClient } from './client'

export type QueueStatus = 'pending' | 'assigned' | 'called' | 'callback' | 'skipped'
export type Outcome =
  | 'no_answer' | 'voicemail' | 'not_interested' | 'interested'
  | 'sale' | 'follow_up' | 'wrong_number' | 'do_not_call' | 'other'

export type WillCallback = '' | 'yes' | 'no' | 'maybe'

/**
 * Méta-données potentiellement présentes dans `custom_data` après un import CSV.
 * Toutes optionnelles — l'importeur ne renseigne que ce qu'il a détecté.
 */
export interface QueueCustomData {
  email?: string
  address?: string
  city?: string
  source_url?: string
  source_host?: string
  all_emails?: string[]
  all_phones?: string[]
  /** Note Google (0-5) et nombre d'avis, si présents dans le CSV importé. */
  rating?: number
  reviews_count?: number
  /** Colonnes non reconnues, conservées telles quelles (clé brute du CSV). */
  extras?: Record<string, string>
}

export interface QueueEntry {
  id: number
  phone: string
  name: string
  notes: string
  // ── Fiche contact structurée (colonnes dédiées, traçées) ──
  company: string
  email: string
  city: string
  address: string
  business_sector: string
  phone_operator: string
  custom_data: QueueCustomData & Record<string, any>
  /** Liste de numéros d'appartenance (pilote les champs de fiche par liste). */
  number_list: number | null
  status: QueueStatus
  assigned_to_id: number | null
  assigned_at: string | null
  called_at: string | null
  callback_at: string | null
  /** Fuseau d'affichage résolu du `callback_at` (campagne › agent › global). */
  schedule_timezone?: string
  callback_note: string
  will_callback: WillCallback
  service_requested: string
  last_outcome: Outcome | ''
  last_ticket_id: number | null
  last_recording_id: number | null
  call_attempts: number
  /** Numéro appartenant à un de nos clients (confirmation à l'appel si la
   *  campagne a `client_warning`). */
  is_client?: boolean
  /** Dernière disposition posée (nom + couleur lisibles) — vide si aucune. */
  last_disposition_name?: string
  last_disposition_color?: string
  created_at: string
  /** ID de la campagne (« fiche ») à laquelle ce numéro appartient. */
  campaign: number | null
}

/** Suggestion d'autocomplétion du composeur (un numéro correspondant). */
export interface QueueSuggestion {
  id: number
  phone: string
  name: string
  company: string
  status: QueueStatus
  last_outcome: string
  last_disposition: string
  last_disposition_color: string
  call_attempts: number
  campaign_name: string
}

export type DialingMode = 'preview' | 'progressive' | 'power' | 'predictive'
export type DistributionStrategy =
  | 'fifo' | 'round_robin' | 'least_busy' | 'fewest_calls'
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived'

/** Stratégie de rotation des numéros présentés (caller-id pool). */
export type CallerIdStrategy = 'round_robin' | 'least_used' | 'random' | 'local_presence'

/** Un numéro présenté du pool d'une campagne (rotation du caller-id). */
export interface CampaignCallerId {
  id: number
  campaign: number
  number: string
  label: string
  is_active: boolean
  order: number
  /** Plafond d'appels/jour pour ce numéro (0 = illimité). */
  max_per_day: number
  // ── État de rotation (lecture seule) ──
  usage_count: number
  last_used_at: string | null
  daily_count: number
  daily_count_date: string | null
  created_at: string
  updated_at: string
}

export type CampaignCallerIdWrite = Partial<
  Pick<CampaignCallerId, 'campaign' | 'number' | 'label' | 'is_active' | 'order' | 'max_per_day'>
>

/** Type de disposition d'appel — pilote le comportement du numéroteur. */
export type DispositionType =
  | 'reschedule'        // rappel programmé
  | 'success_positive'  // clôture (conversion)
  | 'success_negative'  // clôture (joint, pas intéressé)
  | 'failure_retry'     // relance auto
  | 'failure_no_retry'  // clôture définitive (ne plus appeler)

/** Une disposition d'appel configurée sur une campagne. */
export interface CampaignDisposition {
  id: number
  campaign: number
  tenant_id: number
  title: string
  type: DispositionType
  type_label?: string
  color: string
  description: string
  order: number
  is_system: boolean
  is_active: boolean
  /** Conserver l'enregistrement : un appel qualifié ainsi est exempté de la
   *  purge automatique de l'historique. Coché par défaut sur « Vente »/« RDV ». */
  protect_recording: boolean
  /** Compte comme une Vente dans le score de performance (gros coefficient). */
  counts_as_sale: boolean
  /** Compte comme un RDV dans le score de performance (gros coefficient). */
  counts_as_appointment: boolean
  /** Suivi administrateur : l'appel qualifié ainsi crée une fiche à valider
   *  sur la page Suivi d'appels ('' = aucun suivi). */
  follow_up_category: '' | 'recouvrement' | 'fidelisation'
  created_at: string
  updated_at: string
}

export type CampaignDispositionWrite = Partial<
  Pick<CampaignDisposition, 'campaign' | 'title' | 'type' | 'color' | 'description' | 'order' | 'is_active' | 'protect_recording' | 'counts_as_sale' | 'counts_as_appointment' | 'follow_up_category'>
>

/** Une campagne / « fiche » — groupe de numéros pour un projet ou client. */
export interface Campaign {
  id: number
  name: string
  description: string
  team: number
  team_name: string
  tenant_id: number
  is_active: boolean
  status: CampaignStatus
  // ── Planification ──
  is_scheduled: boolean
  scheduled_start: string | null
  scheduled_end: string | null
  // ── Multi-assignation ──
  assigned_groups: number[]
  assigned_users: number[]
  contact_tags: string[]
  // ── Config numérateur ──
  dialing_mode: DialingMode
  answer_timeout_seconds: number
  // ── Contrôles file agent ──
  agent_queue_controls: boolean
  agent_can_skip: boolean
  agent_can_reorder: boolean
  agent_can_delete: boolean
  calls_per_agent: number
  outbound_trunk: number | null
  trunk_name?: string
  caller_id: string
  caller_id_strategy: CallerIdStrategy
  /** Pool multi-numéros (lecture seule ; géré via CallerIdAPI). */
  caller_id_pool?: CampaignCallerId[]
  timezone: string
  /** Format 'HH:MM:SS' ou null si pas de restriction. */
  calling_hours_start: string | null
  calling_hours_end: string | null
  /** Jours autorisés : 0=lundi … 6=dimanche. Vide ⇒ tous les jours. */
  calling_days: number[]
  max_attempts: number
  retry_delay_minutes: number
  retry_on_outcomes: Outcome[]
  wrap_up_seconds: number
  recording_enabled: boolean
  /** Avertissement « client » : confirmation avant d'appeler un numéro is_client. */
  client_warning: boolean
  /** Skip auto des appels non aboutis (occupé/pas de réponse/injoignable). */
  skip_voicemail: boolean
  transcription_enabled: boolean
  script: string
  // Formulaire de rendez-vous réutilisable rattaché (null = aucun).
  appointment_form: number | null
  appointment_form_name?: string
  // Formulaire de VENTE réutilisable rattaché (null = aucun).
  sale_form: number | null
  sale_form_name?: string
  // Script d'appel réutilisable rattaché (null = texte libre `script`).
  script_template: number | null
  script_template_name?: string
  distribution_strategy: DistributionStrategy
  priority: number
  created_by: number | null
  created_at: string
  updated_at: string
  counts?: {
    total: number
    pending: number
    assigned: number
    called: number
    callback: number
  }
  /** Dispositions de la campagne (lecture seule ; gérées via DispositionAPI). */
  dispositions?: CampaignDisposition[]
}

/** Champs modifiables d'une campagne (création / mise à jour). */
export type CampaignWrite = Partial<
  Pick<
    Campaign,
    | 'name' | 'description' | 'team' | 'is_active' | 'status'
    | 'is_scheduled' | 'scheduled_start' | 'scheduled_end'
    | 'assigned_groups' | 'assigned_users' | 'contact_tags'
    | 'dialing_mode' | 'calls_per_agent' | 'outbound_trunk' | 'caller_id'
    | 'caller_id_strategy'
    | 'answer_timeout_seconds'
    | 'timezone' | 'calling_hours_start' | 'calling_hours_end' | 'calling_days'
    | 'max_attempts' | 'retry_delay_minutes' | 'retry_on_outcomes'
    | 'wrap_up_seconds' | 'recording_enabled' | 'client_warning' | 'skip_voicemail' | 'transcription_enabled' | 'script' | 'appointment_form' | 'sale_form' | 'script_template'
    | 'agent_queue_controls' | 'agent_can_skip' | 'agent_can_reorder' | 'agent_can_delete'
    | 'distribution_strategy' | 'priority'
  >
>

/** Choix de config exposés par `/campaigns/options/` pour alimenter les formulaires. */
export interface CampaignOptions {
  statuses: Array<{ value: CampaignStatus; label: string }>
  dialing_modes: Array<{ value: DialingMode; label: string }>
  distribution_strategies: Array<{ value: DistributionStrategy; label: string }>
  caller_id_strategies: Array<{ value: CallerIdStrategy; label: string }>
  outcomes: Array<{ value: Outcome; label: string }>
  disposition_types: Array<{ value: DispositionType; label: string }>
  trunks: Array<{ id: number; name: string; host: string; multichannel?: boolean }>
  weekdays: Array<{ value: number; label: string }>
}

/** Une modification de fiche tracée (journal d'audit). */
export interface FieldChange {
  field: string
  old_value: string
  new_value: string
  changed_by: string | null
  source: 'in_call' | 'admin' | 'import' | 'system'
  changed_at: string
}

export interface ContactLookup {
  phone: string
  queue_entry: QueueEntry | null
  recent_tickets: Array<{
    id: number
    subject: string
    status: string
    priority: number
    created_at: string
  }>
  ticket_count: number
  /** Nom/entreprise identifiés par croisement de nos données (carnet, autres
   *  campagnes, RDV) — présents même si la fiche en cours est vide. */
  identified?: { name: string; company: string; source: string }
  /** Numéro en liste noire (do-not-call) — le softphone bloque l'appel. */
  is_blacklisted?: boolean
}

/** Vérif avant composition : liste noire + statut client (par campagne). */
export interface DialCheck {
  phone: string
  is_blacklisted: boolean
  is_client: boolean
  /** Toutes les campagnes dont ce numéro est CLIENT (avec leur nom). */
  client_campaigns: Array<{ id: number; name: string }>
  /** Mini-fiche pour la popup de confirmation. */
  contact: { name?: string; company?: string }
}

export interface CSVPreview {
  columns: string[]
  detected_format: 'pagesjaunes' | 'generic_fr' | 'generic_en' | 'generic'
  mapping: {
    phone: string | null
    name: string | null
    email: string | null
    address: string | null
    city: string | null
    notes: string | null
    source_url: string | null
    source_host: string | null
    all_emails: string | null
    all_phones: string | null
    extras: string[]
  }
  sample_rows: Array<Record<string, any>>
  total_rows: number
  valid_phone_rows: number
}

export interface CSVImportResult {
  created: number
  skipped: number
  format: string
  mapping: CSVPreview['mapping']
}

export interface QueueStats {
  total: number
  pending: number
  assigned: number
  called: number
  callback: number
  callback_yes: number
  callback_no: number
  callback_maybe: number
  top_services: Array<{ service: string; count: number }>
  outcomes: Array<{ outcome: Outcome; count: number }>
}

/** Une campagne vue par l'AGENT dans « Mes campagnes » (file d'appels). */
export interface AgentCampaign {
  id: number
  name: string
  description: string
  team_name: string
  dialing_mode: DialingMode
  /** Temps de post-appel (s) avant le prochain numéro de la session. */
  wrap_up_seconds: number
  priority: number
  /** Avertissement « client » avant l'appel (confirmation si numéro is_client). */
  client_warning?: boolean
  /** Skip auto des appels non aboutis (défaut activé). */
  skip_voicemail?: boolean
  /** Campagne dans sa plage horaire d'appel (sinon session impossible). */
  callable: boolean
  /** Numéros pending encore éligibles à la pioche. */
  available: number
  /** Numéros déjà en main chez cet agent. */
  my_assigned: number
  /** Rappels personnels arrivés à échéance dans cette campagne. */
  my_callbacks_due: number
}

export interface QueueDiagnostic {
  status: 'no_tenant' | 'no_data' | 'all_taken' | 'awaiting' | 'rate_limited' | 'ok'
  hint: string
  tenant_id: number | null
  role?: string
  my_assigned?: number
  my_callbacks?: number
  tenant_pending?: number
  tenant_total?: number
  rate_limit?: {
    used: number
    limit: number
    remaining: number
    next_slot_at: string | null
  }
}

export const OUTCOME_LABELS: Record<Outcome, string> = {
  no_answer: 'Pas de réponse',
  voicemail: 'Messagerie',
  not_interested: 'Pas intéressé',
  interested: 'Intéressé',
  sale: 'Vente',
  follow_up: 'Suite à donner',
  wrong_number: 'Faux numéro',
  do_not_call: 'Ne pas rappeler',
  other: 'Autre',
}

/** Élément de la vue Contacts unifiée (numéro enrichi carnet + liste noire). */
export interface ContactHubItem {
  id: number
  phone: string
  name: string
  company: string
  email: string
  campaign: number | null
  campaign_name: string
  status: string
  is_client: boolean
  call_attempts: number
  last_outcome: string
  number_list: number | null
  /** Nom de la liste de numéros d'appartenance (vide si aucune). */
  number_list_name: string
  /** Fiche maître (NumberListItem) si matérialisé. Null = contact standalone. */
  source_item: number | null
  /** Valeurs des champs personnalisés (clé → valeur). */
  custom_data: Record<string, any>
  in_carnet: boolean
  carnet_tags: string[]
  is_blacklisted: boolean
  created_at: string
}

/** Un rappel personnel (page Rappels). Plusieurs possibles pour un même numéro. */
export interface Callback {
  id: number
  callback_at: string
  note: string
  created_at: string
  /** Fuseau d'affichage (campagne sinon système). */
  timezone: string
  entry_id: number
  phone: string
  name: string
  campaign_id: number | null
  campaign_name: string
  agent_id: number
  agent_name: string
}

export const QueueAPI = {
  myQueue(): Promise<QueueEntry[]> {
    return apiClient.get('/api/pexitalk/queue/my/').then((r) => r.data)
  },
  diagnostic(): Promise<QueueDiagnostic> {
    return apiClient.get('/api/pexitalk/queue/diagnostic/').then((r) => r.data)
  },
  /** Campagnes accessibles à l'agent avec compteurs (« Mes campagnes »). */
  myCampaigns(): Promise<AgentCampaign[]> {
    return apiClient
      .get('/api/pexitalk/queue/my-campaigns/')
      .then((r) => r.data.campaigns || [])
  },
  /** Prochain numéro à appeler dans la campagne (session d'appels).
   *  `entry: null` = campagne épuisée. Idempotent tant que le numéro en
   *  main n'est pas clôturé. */
  nextInCampaign(campaignId: number): Promise<{ entry: QueueEntry | null; remaining: number }> {
    return apiClient
      .post('/api/pexitalk/queue/next/', { campaign: campaignId })
      .then((r) => r.data)
  },
  myCallbacks(opts: { all?: boolean; related?: boolean; agent?: number; campaign?: number } = {}): Promise<{
    overdue: QueueEntry[]
    today: QueueEntry[]
    upcoming: QueueEntry[]
  }> {
    const params: Record<string, number | string> = {}
    if (opts.all) params.all = 1
    // Périmètre de notification superviseur : équipe + campagnes + ses fiches.
    if (opts.related) params.related = 1
    if (opts.agent) params.agent = opts.agent
    if (opts.campaign) params.campaign = opts.campaign
    return apiClient
      .get('/api/pexitalk/queue/my-callbacks/', { params: Object.keys(params).length ? params : undefined })
      .then((r) => r.data)
  },
  /** Page Rappels — liste PLATE des rappels (plusieurs possibles par numéro). */
  listCallbacks(opts: { all?: boolean; agent?: number; campaign?: number } = {}): Promise<Callback[]> {
    const params: Record<string, number | string> = {}
    if (opts.all) params.all = 1
    if (opts.agent) params.agent = opts.agent
    if (opts.campaign) params.campaign = opts.campaign
    return apiClient
      .get('/api/pexitalk/queue/callbacks/', { params: Object.keys(params).length ? params : undefined })
      .then((r) => r.data.callbacks as Callback[])
  },
  /** Supprime (annule) un rappel : l'agent les siens, un staff n'importe lequel. */
  deleteCallback(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/callbacks/${id}/`).then(() => undefined)
  },
  /** Superviseur+ : affecte une fiche (ex. un rappel) à un agent précis. */
  assign(id: number, agentId: number): Promise<{ entry: QueueEntry }> {
    return apiClient
      .post(`/api/pexitalk/queue/${id}/assign/`, { agent_id: agentId })
      .then((r) => r.data)
  },
  list(params: Record<string, string> = {}): Promise<QueueEntry[]> {
    return apiClient.get('/api/pexitalk/queue/', { params }).then((r) => r.data.results || r.data)
  },
  /** Liste paginée (garde le `count` total) — pour parcourir TOUS les numéros
   *  (pas seulement la page chargée) avec recherche/préfixe côté serveur. */
  listPaged(
    params: Record<string, string | number> = {},
  ): Promise<{ results: QueueEntry[]; count: number; next: string | null; previous: string | null }> {
    return apiClient.get('/api/pexitalk/queue/', { params }).then((r) => {
      const d = r.data
      // Repli si la pagination est désactivée (réponse = tableau brut).
      if (Array.isArray(d)) return { results: d, count: d.length, next: null, previous: null }
      return d
    })
  },
  /** Vue Contacts unifiée : numéros enrichis (carnet + liste noire), paginés. */
  contactsHub(
    params: Record<string, string | number> = {},
  ): Promise<{ results: ContactHubItem[]; count: number }> {
    return apiClient.get('/api/pexitalk/queue/contacts-hub/', { params }).then((r) => {
      const d = r.data
      if (Array.isArray(d)) return { results: d as ContactHubItem[], count: d.length }
      return { results: d.results, count: d.count }
    })
  },
  /** Supprime un contact « standalone » (sans liste/campagne/fiche maître).
   *  Le backend refuse (409) un contact rattaché. */
  deleteEntry(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/${id}/`).then(() => undefined)
  },
  /** Suppression en masse par préfixe de numéro (admin, préfixe ≥ 3). */
  deleteByPrefix(prefix: string, campaign?: number | string): Promise<{ deleted: number }> {
    return apiClient
      .post('/api/pexitalk/queue/delete-by-prefix/', { prefix, campaign: campaign || undefined })
      .then((r) => r.data)
  },
  stats(): Promise<QueueStats> {
    return apiClient.get('/api/pexitalk/queue/stats/').then((r) => r.data)
  },
  create(data: { phone: string; name?: string; notes?: string; campaign?: number; is_client?: boolean }): Promise<QueueEntry> {
    return apiClient.post('/api/pexitalk/queue/', data).then((r) => r.data)
  },
  bulk(
    rows: Array<{ phone: string; name?: string; notes?: string }>,
    opts: { campaign_id?: number } = {},
  ): Promise<{ created: number }> {
    return apiClient
      .post('/api/pexitalk/queue/bulk/', { rows, ...opts })
      .then((r) => r.data)
  },
  importCsv(file: File, opts: { campaign_id?: number; as_client?: boolean } = {}): Promise<CSVImportResult> {
    const fd = new FormData()
    fd.append('file', file)
    if (opts.campaign_id != null) fd.append('campaign_id', String(opts.campaign_id))
    if (opts.as_client) fd.append('as_client', '1')
    return apiClient.post('/api/pexitalk/queue/import-csv/', fd).then((r) => r.data)
  },
  previewCsv(file: File): Promise<CSVPreview> {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient.post('/api/pexitalk/queue/preview-csv/', fd).then((r) => r.data)
  },
  lookup(phone: string): Promise<ContactLookup> {
    return apiClient
      .get('/api/pexitalk/queue/lookup/', { params: { phone } })
      .then((r) => r.data)
  },
  /** Vérif légère + synchrone avant de composer : liste noire + statut client. */
  dialCheck(phone: string, campaign?: number): Promise<DialCheck> {
    return apiClient
      .get('/api/pexitalk/queue/dial-check/', { params: { phone, campaign } })
      .then((r) => r.data)
  },
  /** Autocomplétion du composeur : numéros correspondants (≥ 8 chiffres) avec
   *  nom + dernière disposition + tentatives. Renvoie [] si trop court. */
  suggest(phone: string): Promise<QueueSuggestion[]> {
    return apiClient
      .get('/api/pexitalk/queue/suggest/', { params: { phone } })
      .then((r) => r.data)
  },
  /** Fiche fraîche d'une entrée (l'agent ne lit que ses assignées). */
  get(id: number): Promise<QueueEntry> {
    return apiClient.get(`/api/pexitalk/queue/${id}/`).then((r) => r.data)
  },
  complete(
    id: number,
    payload: {
      /** Outcome legacy OU disposition_id requis (coexistence). */
      outcome?: Outcome
      /** Disposition par campagne choisie (prime sur outcome ; son type
       *  pilote relance/rappel/clôture côté serveur). */
      disposition_id?: number
      /** Date de rappel — `callback_at` aware (legacy) OU `callback_at_local`
       *  heure murale naïve ('YYYY-MM-DDTHH:mm'), localisée serveur dans le
       *  fuseau campagne › agent › global. */
      callback_at?: string
      callback_at_local?: string
      notes?: string
      will_callback?: WillCallback
      service_requested?: string
      ticket_id?: number
      /** Valeurs des champs personnalisés (filtrées serveur sur les champs
       *  définis + éditables de la campagne). */
      custom_data?: Record<string, any>
      /** Rendez-vous obtenu : { data?, scheduled_at_local } — `scheduled_at_local`
       *  est l'heure murale naïve, localisée serveur. `data` est omis quand la
       *  campagne n'a pas de formulaire RDV (simple mémo date/heure). */
      appointment?: {
        data?: Record<string, any>
        scheduled_at_local?: string | null
        scheduled_at?: string | null
        /** Partenaire PexiLine choisi ({id:'', name:''} = sans partenaire). */
        pexiline_partner?: { id: string; name: string }
      }
      /** Vente : { data?, amount? }. Crée une Sale (disposition « vente »). */
      sale?: { data?: Record<string, any>; amount?: string | number | null }
    },
  ): Promise<QueueEntry> {
    return apiClient.post(`/api/pexitalk/queue/${id}/complete/`, payload).then((r) => r.data)
  },
  /** Pose un rappel. `when.callback_at_local` = heure murale naïve (localisée
   *  serveur) ; `when.callback_at` = ISO aware (legacy). */
  callback(
    id: number,
    when: { callback_at?: string; callback_at_local?: string },
    note = '',
  ): Promise<QueueEntry> {
    return apiClient
      .post(`/api/pexitalk/queue/${id}/callback/`, { ...when, note })
      .then((r) => r.data)
  },
  skip(id: number): Promise<QueueEntry> {
    return apiClient.post(`/api/pexitalk/queue/${id}/skip/`).then((r) => r.data)
  },
  /** Met le numéro de la fiche en LISTE NOIRE (globale) et clôture la fiche. */
  /** Liste noire depuis une fiche. `scope` : 'global' (tenant) ou 'campaign'
   *  (propre à la campagne de la fiche). */
  blacklist(id: number, reason = '', scope: 'global' | 'campaign' = 'global'): Promise<QueueEntry> {
    return apiClient.post(`/api/pexitalk/queue/${id}/blacklist/`, { reason, scope }).then((r) => r.data)
  },
  /** Fiche pour un appel manuel (numéro composé hors file) : retrouve la
   *  fiche existante du numéro ou en crée une autonome assignée à l'agent —
   *  même espace de travail tracé que pour la file. */
  manualCard(phone: string): Promise<{ entry: QueueEntry; created: boolean }> {
    return apiClient
      .post('/api/pexitalk/queue/manual-card/', { phone })
      .then((r) => r.data)
  },
  /** Met à jour la fiche contact (en appel) — chaque champ modifié est
   *  journalisé côté serveur (traçabilité). */
  updateContactCard(
    id: number,
    payload: Partial<Pick<QueueEntry, 'name' | 'company' | 'email' | 'city' | 'address' | 'business_sector' | 'phone_operator'>> & {
      custom_data?: Record<string, any>
    },
  ): Promise<{ entry: QueueEntry; changes: number }> {
    return apiClient
      .post(`/api/pexitalk/queue/${id}/contact-card/`, payload)
      .then((r) => r.data)
  },
  /** Journal des modifications de la fiche (qui, quoi, quand, comment). */
  history(id: number): Promise<{ changes: FieldChange[] }> {
    return apiClient.get(`/api/pexitalk/queue/${id}/history/`).then((r) => r.data)
  },
  /** Formulaire RDV rattaché à la campagne du numéro (ou null). */
  appointmentForm(id: number): Promise<{ form: import('./forms').FormTemplate | null }> {
    return apiClient.get(`/api/pexitalk/queue/${id}/appointment-form/`).then((r) => r.data)
  },
  /** Formulaire de VENTE rattaché à la campagne du numéro (ou null). */
  saleForm(id: number): Promise<{ form: import('./forms').FormTemplate | null }> {
    return apiClient.get(`/api/pexitalk/queue/${id}/sale-form/`).then((r) => r.data)
  },
  /** Script d'appel résolu pour ce numéro (réutilisable ou texte libre). */
  script(id: number): Promise<{ name: string; content: string }> {
    return apiClient.get(`/api/pexitalk/queue/${id}/script/`).then((r) => r.data)
  },
  /** Tous les scripts disponibles pour l'agent sur ce numéro (campagne,
   *  assignés, personnels). */
  scripts(id: number): Promise<{ scripts: import('./forms').CallScript[] }> {
    return apiClient.get(`/api/pexitalk/queue/${id}/scripts/`).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/${id}/`)
  },
  /** Marque/retire le statut CLIENT d'une fiche (exclu des appels automatiques). */
  setClient(id: number, client: boolean): Promise<QueueEntry> {
    return apiClient.post(`/api/pexitalk/queue/${id}/client/`, { client }).then((r) => r.data)
  },
  reassign(id: number): Promise<void> {
    return apiClient.post(`/api/pexitalk/queue/${id}/reassign/`)
  },
  heartbeat(): Promise<void> {
    return apiClient.post('/api/pexitalk/monitoring/call-heartbeat/')
  },
  agents(): Promise<any[]> {
    return apiClient.get('/api/pexitalk/monitoring/agents/').then((r) => r.data)
  },
}

export type FieldType =
  | 'text' | 'textarea' | 'number' | 'date' | 'select' | 'checkbox' | 'phone' | 'email'

/** Définition d'un champ personnalisé de fiche (carte d'appel agent). */
export interface CampaignField {
  id: number
  campaign: number
  tenant_id: number
  key: string
  label: string
  field_type: FieldType
  type_label?: string
  required: boolean
  options: string[]
  help_text: string
  order: number
  visible_to_agent: boolean
  editable_by_agent: boolean
  created_at: string
  updated_at: string
}

export type CampaignFieldWrite = Partial<
  Pick<
    CampaignField,
    | 'campaign' | 'key' | 'label' | 'field_type' | 'required'
    | 'options' | 'help_text' | 'order' | 'visible_to_agent' | 'editable_by_agent'
  >
>

/** Une fiche du carnet de contacts (lead/client réutilisable, taggable). */
export interface Contact {
  id: number
  tenant_id: number
  name: string
  phone: string
  email: string
  company: string
  tags: string[]
  custom_data: Record<string, any>
  notes: string
  created_at: string
  updated_at: string
  queue_count?: number
}

export type ContactWrite = Partial<
  Pick<Contact, 'name' | 'phone' | 'email' | 'company' | 'tags' | 'custom_data' | 'notes'>
>

/** Endpoints du carnet de contacts. Montés sous `/api/pexitalk/queue/contacts/`. */
export const ContactAPI = {
  list(params: { q?: string; tag?: string } = {}): Promise<Contact[]> {
    return apiClient
      .get('/api/pexitalk/queue/contacts/', { params })
      .then((r) => r.data.results || r.data)
  },
  tags(): Promise<string[]> {
    return apiClient.get('/api/pexitalk/queue/contacts/tags/').then((r) => r.data.tags || [])
  },
  create(data: ContactWrite & { phone: string }): Promise<Contact> {
    return apiClient.post('/api/pexitalk/queue/contacts/', data).then((r) => r.data)
  },
  update(id: number, data: ContactWrite): Promise<Contact> {
    return apiClient.patch(`/api/pexitalk/queue/contacts/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/contacts/${id}/`)
  },
  importCsv(file: File, opts: { as_client?: boolean } = {}): Promise<{ created: number; updated: number }> {
    const fd = new FormData()
    fd.append('file', file)
    if (opts.as_client) fd.append('as_client', '1')
    return apiClient.post('/api/pexitalk/queue/contacts/import-csv/', fd).then((r) => r.data)
  },
}

/** Endpoints des champs personnalisés. Montés sous `/api/pexitalk/queue/fields/`. */
export const CampaignFieldAPI = {
  list(campaignId?: number): Promise<CampaignField[]> {
    const params = campaignId != null ? { campaign: String(campaignId) } : undefined
    return apiClient
      .get('/api/pexitalk/queue/fields/', { params })
      .then((r) => r.data.results || r.data)
  },
  create(data: CampaignFieldWrite & { campaign: number; key: string; label: string }): Promise<CampaignField> {
    return apiClient.post('/api/pexitalk/queue/fields/', data).then((r) => r.data)
  },
  update(id: number, data: CampaignFieldWrite): Promise<CampaignField> {
    return apiClient.patch(`/api/pexitalk/queue/fields/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/fields/${id}/`)
  },
}

/** Champ personnalisé de fiche défini PAR LISTE (miroir de CampaignField). */
export interface NumberListField {
  id: number
  number_list: number
  tenant_id: number
  key: string
  label: string
  field_type: FieldType
  type_label?: string
  required: boolean
  options: string[]
  help_text: string
  order: number
  visible_to_agent: boolean
  editable_by_agent: boolean
  created_at: string
  updated_at: string
}
export type NumberListFieldWrite = Partial<
  Pick<
    NumberListField,
    | 'number_list' | 'key' | 'label' | 'field_type' | 'required'
    | 'options' | 'help_text' | 'order' | 'visible_to_agent' | 'editable_by_agent'
  >
>

/** Champs perso d'une LISTE. Montés sous `/api/pexitalk/queue/list-fields/`. */
export const NumberListFieldAPI = {
  list(numberListId?: number): Promise<NumberListField[]> {
    const params = numberListId != null ? { number_list: String(numberListId) } : undefined
    return apiClient
      .get('/api/pexitalk/queue/list-fields/', { params })
      .then((r) => r.data.results || r.data)
  },
  create(data: NumberListFieldWrite & { number_list: number; key: string; label: string }): Promise<NumberListField> {
    return apiClient.post('/api/pexitalk/queue/list-fields/', data).then((r) => r.data)
  },
  update(id: number, data: NumberListFieldWrite): Promise<NumberListField> {
    return apiClient.patch(`/api/pexitalk/queue/list-fields/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/list-fields/${id}/`)
  },
}

/** Endpoints des dispositions d'appel par campagne. Montés sous
 *  `/api/pexitalk/queue/dispositions/`. */
export const DispositionAPI = {
  list(campaignId?: number): Promise<CampaignDisposition[]> {
    const params = campaignId != null ? { campaign: String(campaignId) } : undefined
    return apiClient
      .get('/api/pexitalk/queue/dispositions/', { params })
      .then((r) => r.data.results || r.data)
  },
  create(data: CampaignDispositionWrite & { campaign: number; title: string; type: DispositionType }): Promise<CampaignDisposition> {
    return apiClient.post('/api/pexitalk/queue/dispositions/', data).then((r) => r.data)
  },
  update(id: number, data: CampaignDispositionWrite): Promise<CampaignDisposition> {
    return apiClient.patch(`/api/pexitalk/queue/dispositions/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/dispositions/${id}/`)
  },
}

/** Endpoints du pool de numéros présentés (caller-id rotation) par campagne.
 *  Montés sous `/api/pexitalk/queue/caller-ids/`. */
export const CallerIdAPI = {
  list(campaignId?: number): Promise<CampaignCallerId[]> {
    const params = campaignId != null ? { campaign: String(campaignId) } : undefined
    return apiClient
      .get('/api/pexitalk/queue/caller-ids/', { params })
      .then((r) => r.data.results || r.data)
  },
  create(data: CampaignCallerIdWrite & { campaign: number; number: string }): Promise<CampaignCallerId> {
    return apiClient.post('/api/pexitalk/queue/caller-ids/', data).then((r) => r.data)
  },
  update(id: number, data: CampaignCallerIdWrite): Promise<CampaignCallerId> {
    return apiClient.patch(`/api/pexitalk/queue/caller-ids/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/caller-ids/${id}/`)
  },
}

/** Endpoints de gestion des campagnes (« fiches »). Montés sous
 *  `/api/pexitalk/queue/campaigns/`. */
export const CampaignAPI = {
  list(opts: { active?: '1' | '0' | 'all'; team?: number } = {}): Promise<Campaign[]> {
    const params: Record<string, string> = {}
    if (opts.active) params.active = opts.active
    if (opts.team != null) params.team = String(opts.team)
    return apiClient
      .get('/api/pexitalk/queue/campaigns/', { params })
      .then((r) => r.data.results || r.data)
  },
  get(id: number): Promise<Campaign> {
    return apiClient
      .get(`/api/pexitalk/queue/campaigns/${id}/`)
      .then((r) => r.data)
  },
  options(): Promise<CampaignOptions> {
    return apiClient
      .get('/api/pexitalk/queue/campaigns/options/')
      .then((r) => r.data)
  },
  create(data: CampaignWrite & { name: string; team: number }): Promise<Campaign> {
    return apiClient
      .post('/api/pexitalk/queue/campaigns/', data)
      .then((r) => r.data)
  },
  update(id: number, data: CampaignWrite): Promise<Campaign> {
    return apiClient
      .patch(`/api/pexitalk/queue/campaigns/${id}/`, data)
      .then((r) => r.data)
  },
  destroy(id: number): Promise<{ archived?: boolean }> {
    return apiClient
      .delete(`/api/pexitalk/queue/campaigns/${id}/`)
      .then((r) => r.data || {})
  },
  restore(id: number): Promise<Campaign> {
    return apiClient
      .post(`/api/pexitalk/queue/campaigns/${id}/restore/`)
      .then((r) => r.data)
  },
  /** Clone la configuration d'une campagne (file vide, statut brouillon). */
  clone(id: number, opts?: { name?: string; team?: number }): Promise<Campaign> {
    return apiClient
      .post(`/api/pexitalk/queue/campaigns/${id}/clone/`, opts || {})
      .then((r) => r.data)
  },
  /** Suppression DÉFINITIVE (admin/owner) — exige le nom exact en confirmation.
   *  Les numéros ne sont PAS supprimés : ils sont détachés (réassignables). */
  hardDelete(id: number, confirmName: string): Promise<{ deleted: boolean; name: string; entries_detached: number }> {
    return apiClient
      .post(`/api/pexitalk/queue/campaigns/${id}/hard-delete/`, { confirm_name: confirmName })
      .then((r) => r.data)
  },
  /** Matérialise les contacts du carnet matchant les balises de la campagne. */
  syncContacts(id: number): Promise<{ created: number; tags: string[] }> {
    return apiClient
      .post(`/api/pexitalk/queue/campaigns/${id}/sync-contacts/`)
      .then((r) => r.data)
  },
}

// ── Liste noire (do-not-call globale) ───────────────────────────────────────
export interface BlacklistEntry {
  id: number
  phone: string
  phone_e164: string
  reason: string
  /** null = liste noire globale (tenant) ; sinon id de la campagne concernée. */
  campaign: number | null
  created_by_id: number | null
  created_at: string
}

export const BlacklistAPI = {
  /** `campaign` : id (liste noire d'une campagne) ou 'global' (que la globale). */
  list(campaign?: number | 'global'): Promise<BlacklistEntry[]> {
    return apiClient
      .get('/api/pexitalk/queue/blacklist/', { params: campaign ? { campaign } : undefined })
      .then((r) => (r.data.results || r.data) as BlacklistEntry[])
  },
  add(phone: string, reason = '', campaign?: number): Promise<BlacklistEntry> {
    return apiClient
      .post('/api/pexitalk/queue/blacklist/', { phone, reason, campaign: campaign || undefined })
      .then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`/api/pexitalk/queue/blacklist/${id}/`).then(() => undefined)
  },
  /** Import par fichier (CSV / liste de numéros). `reason` obligatoire ;
   * `campaign` = id (liste noire de campagne) ou rien/'global' (globale). */
  importFile(
    file: File, reason: string, campaign?: number | 'global',
  ): Promise<{ added: number; skipped: number }> {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('reason', reason)
    if (campaign && campaign !== 'global') fd.append('campaign', String(campaign))
    return apiClient.post('/api/pexitalk/queue/blacklist/import-file/', fd).then((r) => r.data)
  },
}

/** Une fiche (liste) de numéros — possède ses numéros, liée à N campagnes. */
export interface NumberList {
  id: number
  name: string
  is_active: boolean
  count: number
  /** Campagnes liées (M2M) : la fiche peut alimenter plusieurs campagnes. */
  campaigns: number[]
  campaign_names: string[]
  created_at: string
}
/** Un numéro MAÎTRE d'une fiche (source, indépendant des campagnes). */
export interface NumberListItem {
  id: number
  phone: string
  name: string
  is_active: boolean
  custom_data: Record<string, unknown>
  /** Nb de campagnes où ce numéro est matérialisé. */
  materialized_count: number
  created_at: string
}
export interface NumberListStats {
  id: number
  name: string
  is_active: boolean
  total: number
  by_status: Record<string, number>
  by_outcome: Record<string, number>
  progress: { called: number; pending: number; pct_done: number; avg_attempts: number }
  quality: { valid: number; invalid: number; duplicates: number }
  /** Campagnes liées à la fiche + campagne effectivement mesurée. Quand la fiche
   *  en alimente plusieurs, `requires_campaign` est vrai → le front filtre. */
  campaigns: Array<{ id: number; name: string }>
  campaign_id: number | null
  requires_campaign: boolean
}
/** Filtres d'ajout d'un import CSV ciblé fiche (tous activés par défaut). */
export interface NumberListImportFilters {
  skip_blacklist?: boolean
  skip_invalid?: boolean
  skip_dup_other?: boolean
  as_client?: boolean
}
/** Récap d'un import CSV dans une fiche. */
export interface NumberListImportResult {
  total: number
  added: number
  dup_in_list: number
  dup_other_lists: number
  blacklisted: number
  invalid: number
  format: string
}

export const NumberListAPI = {
  list(): Promise<NumberList[]> {
    return apiClient
      .get('/api/pexitalk/queue/number-lists/')
      .then((r) => (r.data.results || r.data) as NumberList[])
  },
  stats(id: number, campaign?: number): Promise<NumberListStats> {
    const params = campaign ? { campaign } : undefined
    return apiClient
      .get(`/api/pexitalk/queue/number-lists/${id}/stats/`, { params })
      .then((r) => r.data)
  },
  /** Active/désactive un lot (désactivé = exclu de la pioche, réversible). */
  setActive(id: number, is_active: boolean): Promise<NumberList> {
    return apiClient
      .patch(`/api/pexitalk/queue/number-lists/${id}/`, { is_active })
      .then((r) => r.data)
  },
  /** Crée une fiche VIERGE nommée, éventuellement liée à des campagnes. */
  create(data: { name: string; campaigns?: number[] }): Promise<NumberList> {
    return apiClient.post('/api/pexitalk/queue/number-lists/', data).then((r) => r.data)
  },
  /** Supprime une fiche : retire ses numéros de toutes les campagnes liées puis
   *  supprime la fiche + ses numéros maîtres (CDR conservés). */
  remove(id: number): Promise<{ removed_entries: number; items: number }> {
    return apiClient.delete(`/api/pexitalk/queue/number-lists/${id}/`).then((r) => r.data)
  },
  rename(id: number, name: string): Promise<NumberList> {
    return apiClient.patch(`/api/pexitalk/queue/number-lists/${id}/`, { name }).then((r) => r.data)
  },
  /** Définit l'ensemble des campagnes liées (M2M) ; matérialise/retire en diff. */
  setCampaigns(id: number, campaigns: number[]): Promise<{ linked: number; unlinked: number; materialized: number; removed: number; campaigns: number[] }> {
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/set-campaigns/`, { campaigns })
      .then((r) => r.data)
  },
  /** Compat : lie la fiche à UNE seule campagne (null = délier de toutes). */
  move(id: number, campaign: number | null): Promise<{ campaign: number | null }> {
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/move/`, { campaign })
      .then((r) => r.data)
  },
  /** Numéros maîtres de la fiche. */
  items(id: number): Promise<NumberListItem[]> {
    return apiClient.get(`/api/pexitalk/queue/number-lists/${id}/items/`).then((r) => r.data)
  },
  /** Ajoute un numéro maître (propagé à toutes les campagnes liées).
   *  `created=false` ⇒ le numéro était déjà dans la fiche (doublon). */
  addItem(id: number, phone: string, name = ''): Promise<NumberListItem & { created: boolean }> {
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/items/`, { phone, name })
      .then((r) => r.data)
  },
  /** Retire un numéro maître (et ses matérialisations). */
  removeItem(id: number, item: number): Promise<{ removed: boolean }> {
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/remove-item/`, { item })
      .then((r) => r.data)
  },
  /** Active/désactive un numéro maître (propage). */
  toggleItem(id: number, item: number, is_active: boolean): Promise<NumberListItem> {
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/toggle-item/`, { item, is_active })
      .then((r) => r.data)
  },
  /** Importe un CSV DANS cette fiche (filtres configurables) → récap détaillé. */
  importCsv(
    id: number,
    file: File,
    filters: NumberListImportFilters = {},
  ): Promise<NumberListImportResult> {
    const fd = new FormData()
    fd.append('file', file)
    for (const [k, v] of Object.entries(filters)) {
      fd.append(k, v ? '1' : '0')
    }
    return apiClient
      .post(`/api/pexitalk/queue/number-lists/${id}/import-csv/`, fd)
      .then((r) => r.data)
  },
}

/** État d'opt-in d'un agent au dialer serveur (appels automatiques). */
export interface AutodialState {
  /** L'agent a coché « recevoir les appels automatiques ». */
  enabled: boolean
  /** Opt-in ET présence « disponible » → réellement éligible aux appels. */
  ready: boolean
}

export const DialerAPI = {
  /** État courant d'opt-in de l'agent au dialer serveur. */
  availability(): Promise<AutodialState> {
    return apiClient.get('/api/pexitalk/telephony/dialer/availability/').then((r) => r.data)
  },
  /** Active/désactive la réception des appels automatiques (progressif/power/prédictif). */
  setAvailability(enabled: boolean): Promise<AutodialState> {
    return apiClient
      .post('/api/pexitalk/telephony/dialer/availability/', { enabled })
      .then((r) => r.data)
  },
}
